<template>
  <v-card class="primary">
    <v-toolbar
      height="50px"
      class="sub-header"
      color="primary darken-1"
    >
      <v-toolbar-title class="mapTitle">{{ $t("Map View") }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-icon v-if="!isTep" @click="$emit('close')" :dark="isDark">close</v-icon>
    </v-toolbar>
    <v-container pa-0>
      <v-layout wrap>
        <v-flex xs12>
          <div id="map" ref="mapContainer"></div>
        </v-flex>
      </v-layout>
    </v-container>
  </v-card>
</template>

<script>
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { mapGetters, mapActions, mapMutations } from "vuex";

export default {
  name: "MapComponent",
  props: {
    isDark: {
      type: Boolean,
      default: true
    },
    isTep: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      map: null
    };
  },
  computed: {
    ...mapGetters([
      "getSelectedPlantId", 
      "getAllPlantCoordinates", 
      "getRules"
    ]),
    markers() {
      return this.getAllPlantCoordinates.filter(plant => 
        plant.position.lat != null && 
        plant.position.lng != null
      );
    },
    geoJsonFeatures() {
      return {
        type: "FeatureCollection",
        features: this.markers.map(plant => ({
          type: "Feature",
          properties: { 
            id: plant.plantId, 
            name: plant.plantName 
          },
          geometry: {
            type: "Point",
            coordinates: [plant.position.lng, plant.position.lat]
          }
        }))
      };
    }
  },
  mounted() {
    this.initializeMap();
  },
  beforeDestroy() {
    // Clean up resources when component is destroyed
    if (this.map) {
      this.map.remove();
    }
  },
  methods: {
    ...mapMutations(["SET_SELECTED_COMPONENT_MENU"]),
    ...mapActions(["fetchActivePlantDetails"]),
    initializeMap() {
      mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_MAP_KEY;
      
      const container = this.$refs.mapContainer;
      this.map = new mapboxgl.Map({
        container,
        style: this.isDark ? "mapbox://styles/etitsupport/cm8il7tga00ri01qs37e8e3dd" : "mapbox://styles/etitsupport/cm9bews5o003x01s8b1nwazdm",
        center: [75.191955, 29.859122],
        zoom: this.isTep ? 2 : 6
      });
      
      this.map.addControl(new mapboxgl.FullscreenControl());
      this.map.addControl(
        new mapboxgl.NavigationControl({ showZoom: true }), 
        "bottom-right"
      );
      
      this.map.on("load", () => {
        this.addMapLayers();
        this.setupEventListeners();
        this.fitMapToBounds();
      });
    },
    addMapLayers() {
      // Add source for plants
      this.map.addSource("plants", {
        type: "geojson",
        data: this.geoJsonFeatures,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50
      });
      
      this.map.addLayer({
        id: "clusters",
        type: "circle",
        source: "plants",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": [
            "step", 
            ["get", "point_count"], 
            "#51bbd6", 10, 
            "#f1f075", 50, 
            "#f28cb1"
          ],
          "circle-radius": [
            "step", 
            ["get", "point_count"], 
            15, 10, 
            20, 50, 
            30
          ]
        }
      });
      
      this.map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "plants",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 12
        }
      });
      
      this.map.addLayer({
        id: "unclustered-point",
        type: "circle",
        source: "plants",
        filter: ["!has", "point_count"],
        paint: {
          "circle-color": "#11b4da",
          "circle-radius": 6,
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff"
        }
      });
    },
    setupEventListeners() {
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false
      });
      
      this.map.on("mouseenter", "unclustered-point", (e) => {
        this.map.getCanvas().style.cursor = "pointer";
        
        const coordinates = e.features[0].geometry.coordinates.slice();
        const plantName = e.features[0].properties.name;
        const description = `<div class="popup"><h3 class="popup description-title">${plantName}</h3></div>`;
        
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        
        popup.setLngLat(coordinates).setHTML(description).addTo(this.map);
      });
      
      this.map.on("mouseleave", "unclustered-point", () => {
        this.map.getCanvas().style.cursor = "";
        popup.remove();
      });
      
      this.map.on("click", "unclustered-point", (e) => {
        const plantId = e.features[0].properties.id;
        this.navigateToPlantDashboard(plantId);
      });
      
      this.map.on("click", "clusters", (e) => {
        const features = this.map.queryRenderedFeatures(e.point, {
          layers: ["clusters"]
        });
        
        const clusterId = features[0].properties.cluster_id;
        
        this.map.getSource("plants").getClusterExpansionZoom(
          clusterId,
          (err, zoom) => {
            if (err) return;
            
            this.map.easeTo({
              center: features[0].geometry.coordinates,
              zoom: zoom
            });
          }
        );
      });
      
      this.map.on("mouseenter", "clusters", () => {
        this.map.getCanvas().style.cursor = "pointer";
      });
      
      this.map.on("mouseleave", "clusters", () => {
        this.map.getCanvas().style.cursor = "";
      });
    },
    fitMapToBounds() {
      if (!this.markers.length) return;
      
      const bounds = new mapboxgl.LngLatBounds();
      
      this.markers.forEach(plant => {
        bounds.extend([plant.position.lng, plant.position.lat]);
      });
      
      this.map.fitBounds(bounds, {
        padding: 50,
        maxZoom: 14
      });
    },
    async navigateToPlantDashboard(plantId) {
      this.SET_SELECTED_COMPONENT_MENU("plantOverview");
      await this.fetchActivePlantDetails(plantId);
      
      const path = this.getRules(["rule", "access_config"])
        ? this.getRules(["rule", "access_config", "plantOverview", "path"])
        : "/plantOverview";
        
      this.$router.push(path);
    }
  },
  watch: {
    isDark() {
      this.initializeMap();
    }
  }
};
</script>

<style>
#map {
  height: 400px;
  width: 100%;
}
.mapTitle {
  color: var(--v-extraGrey-base);
  font-size: 18px;
}
.popup {
  min-width: 100px;
  max-width: 300px;
}

.description-title {
  text-align: left;
  color: #000;
}

.mapboxgl-canvas {
  padding: 0;
}
</style>